import { isImportingCococartAssetsAtom } from '@/atoms/connectedAccounts'
import { ImportedProducts } from '@/components/products/ImportedProducts'
import { useAtomValue } from 'jotai'
import { createFileRoute } from '@tanstack/react-router'
import { EmptyAssets } from '@/components/assets/EmptyAssets'
import { ImportingAssets } from '@/components/assets/ImportingAssets'
import { AppEvents, AppEventTypes } from '@/types/AppEventTypes'
import { useEffect, useState } from 'react'
import { useGetMediaAssets } from '@/hooks/mediaAssets/useGetMediaAssets'
import { useInjection } from 'inversify-react'
import EventEmitter from 'eventemitter3'
import { useDebounce } from 'use-debounce'
import { AppLoader } from '@/components/AppLoader'

export const Route = createFileRoute('/_app/assets')({
  component: AssetsSelection,
})

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col flex-1 h-full p-6 md:p-0 items-center justify-center w-full">
      {children}
    </div>
  )
}

function AssetsSelection() {
  const isImportingCococartAssets = useAtomValue(isImportingCococartAssetsAtom)
  const [search, setSearch] = useState('')
  const [searchValue] = useDebounce(search, 500)
  const { assets, isLoading, total, fetchNextPage, refetch } = useGetMediaAssets(searchValue)

  const eventBus = useInjection<EventEmitter<AppEventTypes>>('EventBus')

  useEffect(() => {
    let ignore = false

    const handler = async () => {
      if (ignore) {
        return
      }

      await refetch()
    }

    eventBus.on(AppEvents.refetchAssets, handler)

    return () => {
      ignore = true
      eventBus.off(AppEvents.refetchAssets, handler)
    }
  }, [refetch, eventBus])

  if (isImportingCococartAssets) {
    return (
      <Wrapper>
        <ImportingAssets />
      </Wrapper>
    )
  }

  if (isLoading) {
    return (
      <Wrapper>
        <AppLoader />
      </Wrapper>
    )
  }

  if (total === 0 && !isLoading && !searchValue?.length) {
    return (
      <Wrapper>
        <EmptyAssets />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <ImportedProducts
        search={search}
        setSearch={setSearch}
        assets={assets}
        total={total}
        isLoading={isLoading}
        fetchNextPage={fetchNextPage}
      />
    </Wrapper>
  )
}
